import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumbs from '../../components/Breadcrumbs';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { ArrowRight, CheckCircle, Car } from 'lucide-react';

const Automotive = () => {
  return (
    <div>
      <SEO 
        title="Automotive Industry Trade Compliance Services"
        description="Expert trade compliance for automotive manufacturers and suppliers. Navigate tariffs, USMCA rules of origin, customs regulations, and export controls for vehicle components."
        keywords="automotive trade compliance, USMCA automotive, vehicle export regulations, automotive tariffs, parts classification"
        path="/industries/automotive"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Services', path: '/services' },
        { label: 'Automotive Industry', path: '/industries/automotive' }
      ]} />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Automotive Industry Trade Compliance
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              The automotive industry navigates complex tariffs, USMCA rules of origin, and global supply chain compliance. Our specialized services help manufacturers and suppliers optimize duty savings while ensuring regulatory compliance.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Automotive Compliance Challenges</h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-red-50 rounded-lg p-8">
              <Car className="h-10 w-10 text-red-700 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">USMCA Rules of Origin</h3>
              <p className="text-gray-700">Complex regional value content and vehicle classification requirements for duty-free treatment under USMCA.</p>
            </div>
            <div className="bg-red-50 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Tariff Classification</h3>
              <p className="text-gray-700">Thousands of automotive parts with specific HTS classifications affecting duty rates and trade agreement eligibility.</p>
            </div>
            <div className="bg-red-50 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Section 232 & 301 Tariffs</h3>
              <p className="text-gray-700">Additional tariffs on steel, aluminum, and certain imported vehicles require careful compliance and exception tracking.</p>
            </div>
            <div className="bg-red-50 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Global Supply Chains</h3>
              <p className="text-gray-700">Multi-tier supplier networks crossing multiple countries require coordinated compliance programs.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Automotive Trade Services</h2>
          
          <div className="max-w-4xl mx-auto space-y-6">
            {[
              'USMCA rules of origin determination and documentation',
              'HTS classification for automotive parts and components',
              'Regional value content calculations',
              'Foreign Trade Zone feasibility for automotive operations',
              'Duty drawback program implementation',
              'Section 232 and 301 tariff mitigation strategies',
              'Supplier compliance program development',
              'Import/export documentation and recordkeeping'
            ].map((service, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-lg text-gray-800">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Automotive Trade Compliance Consultation
          </h2>
          <p className="text-xl text-slate-100 mb-10 max-w-2xl mx-auto">
            Optimize your automotive supply chain compliance and reduce duty costs with expert guidance.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Automotive;